import { useState, useRef, useCallback } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { streamChat } from "@/lib/streamChat";
import api, { classifyError } from "@/lib/api";
// Manages a streaming multi-turn chat. Messages: { role, content, metadata?, streaming?, error? }
export function useChat({ initialConversationId = null, initialMessages = [] } = {}) {
  const { language } = useLanguage();
  const [messages, setMessages] = useState(initialMessages);
  const [streaming, setStreaming] = useState(false);
  const [followups, setFollowups] = useState([]);
  const [conversationId, setConversationId] = useState(initialConversationId);
  const convRef = useRef(initialConversationId); // latest id for use inside async callbacks
  const lastTurn = useRef(null);

  const setConvId = useCallback((id) => {
    if (id && id !== convRef.current) {
      convRef.current = id;
      setConversationId(id);
    }
  }, []);

  const run = useCallback(
    async (clean, mode, deepResearch = false) => {
      const history = messages
        .filter((m) => !m.error && m.content)
        .map((m) => ({ role: m.role, content: m.content }));
      setFollowups([]);
      setMessages((prev) => [
        ...prev,
        { role: "user", content: clean },
        { role: "assistant", content: "", streaming: true },
      ]);
      setStreaming(true);
      lastTurn.current = { prompt: clean, mode, deepResearch };

      let acc = "";
      await streamChat({
        prompt: clean,
        mode,
        language,
        conversationId: convRef.current,
        history,
        deepResearch,
        onStart: (d) => setConvId(d?.conversation_id),
        onResearch: (d) => {
          setMessages((prev) => {
            const c = [...prev];
            const last = c[c.length - 1];
            if (last && last.role === "assistant" && !last.content) {
              c[c.length - 1] = { ...last, researching: true, researchSensitive: !!(d && d.sensitive) };
            }
            return c;
          });
        },
        onDelta: (delta) => {
          acc += delta;
          setMessages((prev) => {
            const c = [...prev];
            const last = c[c.length - 1];
            c[c.length - 1] = { ...last, content: last.content + delta, researching: false };
            return c;
          });
        },
        onDone: (d) => {
          setConvId(d?.conversation_id);
          setMessages((prev) => {
            const c = [...prev];
            const last = c[c.length - 1];
            c[c.length - 1] = { ...last, streaming: false, metadata: d?.metadata };
            return c;
          });
          setStreaming(false);
          if (acc.trim()) {
            window.dispatchEvent(new CustomEvent("evermin:ai-used"));
            api
              .post("/ai/followups", { prompt: clean, answer: acc.slice(0, 6000), language })
              .then(({ data }) => setFollowups(data.suggestions || []))
              .catch(() => setFollowups([]));
          }
        },
        onError: (e) => {
          const info = e && e.kind === "limit"
            ? { kind: "limit", tier: e.tier || "free" }
            : { kind: (e && e.kind) || "temporary" };
          setMessages((prev) => {
            const c = [...prev];
            const last = c[c.length - 1];
            c[c.length - 1] = { ...last, streaming: false, error: true, errorInfo: info };
            return c;
          });
          setStreaming(false);
        },
      });

      return convRef.current;
    },
    [messages, language, setConvId]
  );

  const send = useCallback(
    async (prompt, mode = "understand", deepResearch = false) => {
      const clean = (prompt || "").trim();
      if (!clean || streaming) return convRef.current;
      return run(clean, mode, deepResearch);
    },
    [run, streaming]
  );

  // Image / document turn: sends the file (+ optional text) to the vision endpoint
  // and renders the answer as a normal assistant message. Works for guests too.
  const sendImage = useCallback(
    async (files, prompt = "") => {
      const list = Array.isArray(files) ? files.filter(Boolean) : (files ? [files] : []);
      if (!list.length || streaming) return;
      const clean = (prompt || "").trim();
      const previewUrls = list.map((f) => URL.createObjectURL(f));
      setFollowups([]);
      setMessages((prev) => [
        ...prev,
        { role: "user", content: clean, images: previewUrls },
        { role: "assistant", content: "", streaming: true },
      ]);
      setStreaming(true);
      const form = new FormData();
      list.forEach((f) => form.append("files", f));
      form.append("prompt", clean);
      form.append("language", language);
      try {
        const { data } = await api.post("/ai/analyze", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMessages((prev) => {
          const c = [...prev];
          c[c.length - 1] = { ...c[c.length - 1], content: data.answer, streaming: false, metadata: data.metadata };
          return c;
        });
        window.dispatchEvent(new CustomEvent("evermin:ai-used"));
      } catch (err) {
        const info = classifyError(err); // 429 also auto-opens the upgrade modal via the interceptor
        setMessages((prev) => {
          const c = [...prev];
          c[c.length - 1] = { ...c[c.length - 1], streaming: false, error: true, errorInfo: info };
          return c;
        });
      } finally {
        setStreaming(false);
      }
    },
    [streaming, language]
  );

  const retry = useCallback(async () => {
    if (streaming || !lastTurn.current) return;
    const { prompt, mode, deepResearch } = lastTurn.current;
    setMessages((prev) => {
      const c = [...prev];
      if (c.length && c[c.length - 1].role === "assistant" && c[c.length - 1].error) c.pop();
      if (c.length && c[c.length - 1].role === "user") c.pop();
      return c;
    });
    await run(prompt, mode, deepResearch);
  }, [run, streaming]);

  return { messages, setMessages, send, sendImage, retry, streaming, followups, conversationId };
}
